/**
 * KanbanBoardContainer - Smart container that connects KanbanBoard to KanbanStore.
 * @implements FS-005.9.4.1 - Subscription Integration
 * @implements FS-005.9.5.1 - Empty Board State
 * @implements FS-005.9.5.2 - Loading State
 * @implements FS-005.9.5.3 - Error State
 * @implements US-117 - KanbanStore Integration
 *
 * This component bridges the KanbanStore MobX observable with the KanbanBoard
 * presentational component. It handles:
 * - Loading state with skeleton columns
 * - Error state with retry button
 * - Empty state when no tickets exist
 * - Real-time subscription updates (via useScrumMaster)
 */

import { useCallback } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { ViewKanban as ViewKanbanIcon } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useScrumMaster } from '@/hooks';
import { COLUMN_ORDER } from '@/utils/kanban';
import { KanbanBoard } from './KanbanBoard';

/**
 * Props for KanbanBoardContainer component.
 */
export interface KanbanBoardContainerProps {
  /** Project ID for fetching kanban state */
  projectId: string;
}

/**
 * Column header display names.
 * @implements FS-005.9.1.1 - Column Structure
 */
const COLUMN_DISPLAY_NAMES: Record<string, string> = {
  backlog: 'Backlog',
  consolidation: 'Consolidation',
  todo: 'Todo',
  inProgress: 'In Progress',
  review: 'Review',
  qa: 'QA',
  done: 'Done',
};

/**
 * Loading skeleton component for the kanban board.
 * Displays skeleton columns to maintain layout during loading.
 * @implements FS-005.9.5.2 - Loading State
 */
const KanbanLoadingSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(7, minmax(200px, 1fr))',
      gap: 1.5,
      flex: 1,
      px: 2,
      py: 1.5,
    }}
    data-testid="kanban-loading-skeleton"
  >
    {COLUMN_ORDER.map((column) => (
      <Box
        key={column}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.100',
          borderRadius: 1,
          p: 1,
          minHeight: 200,
        }}
      >
        {/* Column header skeleton */}
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
        {/* Card skeletons */}
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={80} />
          {column === 'backlog' && <Skeleton variant="rounded" height={80} />}
        </Stack>
      </Box>
    ))}
  </Box>
);

/**
 * Error state component with retry button.
 * @implements FS-005.9.5.3 - Error State
 */
interface ErrorStateProps {
  error: string;
  onRetry: () => void;
}

const ErrorState = ({ error, onRetry }: ErrorStateProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      p: 4,
    }}
    data-testid="kanban-error-state"
  >
    <Alert
      severity="error"
      sx={{ mb: 2, maxWidth: 500 }}
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
    >
      {error}
    </Alert>
  </Box>
);

/**
 * Empty board state component.
 * Shows when project has no tickets.
 * @implements FS-005.9.5.1 - Empty Board State
 */
const EmptyBoardState = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
    }}
  >
    {/* Column headers remain visible */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(200px, 1fr))',
        gap: 1.5,
        px: 2,
        py: 1.5,
      }}
    >
      {COLUMN_ORDER.map((column) => (
        <Box
          key={column}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 1,
            minHeight: 200,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              {COLUMN_DISPLAY_NAMES[column] || column}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'grey.300',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              0
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>

    {/* Centered empty message */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        p: 4,
      }}
      data-testid="kanban-empty-state"
    >
      <ViewKanbanIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" gutterBottom color="text.secondary">
        No Tickets Yet
      </Typography>
      <Typography variant="body2" color="text.disabled" textAlign="center">
        Tickets will appear here once they are created through
        <br />
        conversations with the AI PM or the roadmap.
      </Typography>
    </Box>
  </Box>
);

/**
 * KanbanBoardContainer - Smart container for the kanban board.
 *
 * @implements FS-005.9.4.1 - Subscription Integration
 * @implements FS-005.9.5.1 - Empty Board State
 * @implements FS-005.9.5.2 - Loading State
 * @implements FS-005.9.5.3 - Error State
 * @implements US-117 - KanbanStore Integration
 *
 * Real-time updates via subscription (handled by useScrumMaster).
 */
export const KanbanBoardContainer = observer(
  ({ projectId }: KanbanBoardContainerProps) => {
    // Connect to KanbanStore via useScrumMaster hook
    const { store } = useScrumMaster({ projectId });

    // Extract state from store for reactive rendering
    const { tickets, ticketsByColumn, validationStatuses } = store.server;
    const { loading, error } = store.local;

    // Convert tickets record to array for KanbanBoard
    const ticketsArray = Object.values(tickets);

    // Check if board is empty (no tickets)
    const isEmpty = ticketsArray.length === 0;

    // Handle retry on error
    const handleRetry = useCallback(() => {
      store.clearError();
      store.setLoading(true);
      // The useScrumMaster hook will re-fetch on next mount/render
      // For now, we trigger a page reload as a simple solution
      window.location.reload();
    }, [store]);

    // Render loading state
    if (loading && isEmpty) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          <KanbanLoadingSkeleton />
        </Box>
      );
    }

    // Render error state
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          <ErrorState error={error} onRetry={handleRetry} />
        </Box>
      );
    }

    // Render empty state
    if (isEmpty && !loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          }}
        >
          <EmptyBoardState />
        </Box>
      );
    }

    // Render populated board
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        }}
      >
        <KanbanBoard
          projectId={projectId}
          tickets={ticketsArray}
          ticketsByColumn={ticketsByColumn}
          validationStatuses={validationStatuses}
        />
      </Box>
    );
  },
);

// Display name for React DevTools
KanbanBoardContainer.displayName = 'KanbanBoardContainer';
